import { DataSourceExecutionEvent, LookupModelSearchEvent, ChangeEvent, Textbox, Checkbox, Component, DropdownItem, TableRow, TableRowDisplayEvent, DropdownSelectionEvent, TableRowBeforeSaveEvent } from "@mcleod/components";
import { FreightMatchingControl } from "./FreightMatchingControl";
import { FreightMatchingProfile } from "./FreightMatchingProfile";
import { FmDataOutputTable } from "./FmDataOutputTable";
import { FmProfileDataOutputTable } from "./FmProfileDataOutputTable";
import { ModelRow } from "@mcleod/core";

export enum FmDataOutputType {
    SETTLEMENT,
    CARRIER,
    LOAD
}

//See note in ModelFreightMatchingDataOutput for why not using dynamic metadata
const CARRIER_TABLES = [
    new DropdownItem("payee"),
    new DropdownItem("drs_payee"),
    new DropdownItem("contact"),
    new DropdownItem("users")
]

const LOAD_TABLES = [
    new DropdownItem("movement"),
    new DropdownItem("stop"),
    new DropdownItem("location"),
    new DropdownItem("orders"),
    new DropdownItem("customer"),
    new DropdownItem("equipment_type"),
    new DropdownItem("users"),
    new DropdownItem("freight_group_item"),
    new DropdownItem("equip_match_detail")
]

const SETTLEMENT_TABLES = [
    new DropdownItem("settlement"),
    new DropdownItem("drs_pending_deduct"),
    new DropdownItem("drs_settle_hist"),
    new DropdownItem("drs_deduct_hist"),
    new DropdownItem("users")
]

export class FreightMatchingUtil {
    private isProfile: boolean = false;

    constructor(private readonly layout: FreightMatchingControl | FreightMatchingProfile) {
        this.addListeners();
    }

    addListeners() {
        this.layout.sourceFreightMatchingDataOutputCarrier.addBeforeExecutionListener(this.sourceFreightMatchingDataOutputCarrierBeforeExecution.bind(this));
        this.layout.sourceFreightMatchingDataOutputLoad.addBeforeExecutionListener(this.sourceFreightMatchingDataOutputLoadBeforeExecution.bind(this));
        this.layout.sourceFreightMatchingDataOutputSettlement.addBeforeExecutionListener(this.sourceFreightMatchingDataOutputSettlementBeforeExecution.bind(this));
        this.layout.textboxAutoRateConfirmTemplate.addBeforeLookupModelSearchListener(this.textboxAutoRateConfirmTemplateBeforeLookupModelSearch.bind(this));
        this.layout.checkboxEnableComment.addChangeListener(this.checkboxEnableCommentOnChange.bind(this));
        this.layout.textboxCommentType.addChangeListener(this.textboxCommentTypeOnChange.bind(this));
        this.layout.checkboxCreateAvailableTractors.addChangeListener(this.checkboxCreateAvailableTractorsOnChange.bind(this));
        this.layout.checkboxEnableCarrierOffer.addChangeListener((e) => this.checkboxEnableCarrierOfferOnChange(e));
        this.layout.checkboxEnableMilestones.addChangeListener((e) => this.checkboxEnableMilestonesOnChange(e));
        this.layout.checkboxEnableLoadBooking.addChangeListener((e) => this.checkboxEnableLoadBookingOnChange(e));
        this.layout.checkboxSendArc.addChangeListener(this.checkboxSendArcOnChange.bind(this));
        this.layout.checkboxIsPayRateProvider.addChangeListener(this.checkboxIsPayRateProviderOnChange.bind(this));
        this.layout.checkboxEnabledCarrierUnassign.addChangeListener(this.checkboxEnabledCarrierUnassignOnChange.bind(this));

        // Data output table logic
        this.initializeDataOutput((this.layout.layoutCarrierDataOutput as FmDataOutputTable | FmProfileDataOutputTable), FmDataOutputType.CARRIER);
        this.initializeDataOutput((this.layout.layoutSettlementDataOutput as FmDataOutputTable | FmProfileDataOutputTable), FmDataOutputType.SETTLEMENT);
        this.initializeDataOutput((this.layout.layoutLoadDataOutput as FmDataOutputTable | FmProfileDataOutputTable), FmDataOutputType.LOAD);

        this.layout.addLayoutLoadListener(() => {
            this.initializeUIState();
        });
    }

    initializeDataOutput(layout: FmDataOutputTable | FmProfileDataOutputTable, type: FmDataOutputType) {
        this.isProfile = layout instanceof FmProfileDataOutputTable;
        layout.initialize(type);
        const historicalColumn = layout.tableFmDataOutput.getColumnByCaption("Historical");
        const listenerEnabledColumn = layout.tableFmDataOutput.getColumnByCaption("Listener Enabled");
        switch (type) {
            case FmDataOutputType.SETTLEMENT:
                layout.tableFmDataOutput.removeColumn(historicalColumn);
                layout.tableFmDataOutput.removeColumn(listenerEnabledColumn);
                layout.setTableItems(SETTLEMENT_TABLES);
                break;
            case FmDataOutputType.CARRIER:
                layout.tableFmDataOutput.removeColumn(historicalColumn);
                layout.setTableItems(CARRIER_TABLES);
                break;
            case FmDataOutputType.LOAD:
                layout.setTableItems(LOAD_TABLES);
                break;
        }
        layout.tableFmDataOutput.addRowDisplayListener(this.tableFmDataOutputOnRowDisplay(layout));
        layout.tableFmDataOutput.addRowBeforeSaveListener(this.tableFmDataOutputBeforeRowSave(layout));
    }

    initializeUIState() {
        this.layout.sourceFreightMatchingDataOutputCarrier.search();
        this.layout.sourceFreightMatchingDataOutputLoad.search();
        this.layout.sourceFreightMatchingDataOutputSettlement.search();
        const mockChangeEvent = (comp: Component) => {
            let value = null;
            if (comp instanceof Checkbox) {
                value = comp.checked;
            }
            else if (comp instanceof Textbox) {
                value = comp.text;
            }
            return new ChangeEvent(comp, null, value, null);
        }
        this.checkboxEnableCommentOnChange(mockChangeEvent(this.layout.checkboxEnableComment));
        this.textboxCommentTypeOnChange(mockChangeEvent(this.layout.textboxCommentType));
        this.checkboxCreateAvailableTractorsOnChange(mockChangeEvent(this.layout.checkboxCreateAvailableTractors));
        this.checkboxEnableCarrierOfferOnChange(mockChangeEvent(this.layout.checkboxEnableCarrierOffer));
        this.checkboxEnableMilestonesOnChange(mockChangeEvent(this.layout.checkboxEnableMilestones));
        this.checkboxEnableLoadBookingOnChange(mockChangeEvent(this.layout.checkboxEnableLoadBooking));
        this.checkboxSendArcOnChange(mockChangeEvent(this.layout.checkboxSendArc));
        this.checkboxIsPayRateProviderOnChange(mockChangeEvent(this.layout.checkboxIsPayRateProvider));
        this.checkboxEnabledCarrierUnassignOnChange(mockChangeEvent(this.layout.checkboxEnabledCarrierUnassign));
    }

    private setVisibleComponents(components: (Component | undefined)[], v: boolean) {
        components.forEach(c => { if (c) c.visible = v; });
    }
    private setRequiredComponents(components: (Component | undefined)[], v: boolean) {
        components.forEach(c => { if (c && 'required' in c) (c as any).required = v && !this.isProfile; });
    }
    private isChecked(component?: Checkbox): boolean {
        return !!(component && component.checked);
    }
    private text(component?: Textbox): string {
        return component?.text ?? "";
    }

    tableFmDataOutputOnRowDisplay(layout: FmDataOutputTable | FmProfileDataOutputTable) {
        return (event: TableRowDisplayEvent): void => {
            const row: TableRow = event.getTableRow();
            const textboxFieldName = row.findComponentById("textboxFieldName") as Textbox;
            if(row === layout.tableFmDataOutput.quickAddRow) {
                const textboxTableName = row.findComponentById("textboxTableName") as Textbox;
                if (textboxTableName) {
                    textboxTableName.items = layout.getTableItems();
                }
                if (textboxFieldName && textboxTableName) {
                    textboxTableName.addAfterDropdownSelectionListener((event: DropdownSelectionEvent) => {
                        if (event.hasSelection()) {
                            textboxFieldName.enabled = true;
                        } else {
                            textboxFieldName.enabled = false;
                        }
                    })
                }
                textboxFieldName.addBeforeLookupModelSearchListener(this.textboxFieldNameBeforeLookupModelSearch(layout));
            } else if (textboxFieldName) {
                textboxFieldName.lookupModelDisabled = true;
            }
        }
    }

    textboxFieldNameBeforeLookupModelSearch(layout: FmDataOutputTable | FmProfileDataOutputTable) {
        return (event: LookupModelSearchEvent) => {
            const textboxFieldName = event.target as Textbox;
            const selectedTable = textboxFieldName.boundRow?.get("table_name");
            if (selectedTable) {
                event.filter.table_name = selectedTable;
            }

            //there seems to be a bug that causes "in " to be prepended to the first field in the list when using this trick
            //not going to investigate or report due to this basically never being used, but dummy field fixes it
            let notInClause = "not in (DUMMY_FIELD,";
            layout.mainDataSource.data.forEach((row) => {
                if (row.get("table_name") === selectedTable) {
                    notInClause += row.get("field_name") + ",";
                }
            })
            //remove end comma
            event.filter.field_name = notInClause.slice(0, -1) + ")";
        };
    }

    tableFmDataOutputBeforeRowSave(layout: FmDataOutputTable | FmProfileDataOutputTable) {
        return (event: TableRowBeforeSaveEvent) => {
            const tableRow: TableRow = event.target as TableRow;
            const modelRow: ModelRow = tableRow.data;
            modelRow.set("data_type", this.getOutputTypeString(layout));
            if (layout instanceof FmProfileDataOutputTable) {
                modelRow.set("fm_profile_id", this.layout.activeRow.get("id"));
            }
        };
    }

    getOutputTypeString(layout: FmDataOutputTable | FmProfileDataOutputTable) {
        switch (layout.getType()) {
            case FmDataOutputType.CARRIER:
                return "C";
            case FmDataOutputType.LOAD:
                return "L";
            case FmDataOutputType.SETTLEMENT:
                return "S";
            default:
                return null;
        }
    }

    sourceFreightMatchingDataOutputLoadBeforeExecution(event: DataSourceExecutionEvent) {
        event.filter.data_type = "L";
        if (this.layout instanceof FreightMatchingProfile && this.layout.activeRow) {
            if(this.layout.activeRow._appending){
                event.filter.is_active = "Y";
            }else {
                event.filter.fm_profile_id = this.layout.activeRow.get("id");
            }
        }
    }

    sourceFreightMatchingDataOutputSettlementBeforeExecution(event: DataSourceExecutionEvent) {
        event.filter.data_type = "S";
        if (this.layout instanceof FreightMatchingProfile && this.layout.activeRow) {
            if(this.layout.activeRow._appending){
                event.filter.is_active = "Y";
            }else {
                event.filter.fm_profile_id = this.layout.activeRow.get("id");
            }
        }
    }

    sourceFreightMatchingDataOutputCarrierBeforeExecution(event: DataSourceExecutionEvent) {
        event.filter.data_type = "C";
        if (this.layout instanceof FreightMatchingProfile && this.layout.activeRow) {
            if(this.layout.activeRow._appending){
                event.filter.is_active = "Y";
            }else {
                event.filter.fm_profile_id = this.layout.activeRow.get("id");
            }
        }
    }

    textboxAutoRateConfirmTemplateBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.document_type = 'Z';
        event.filter.template_number = '>0'
    }

    checkboxEnableCommentOnChange(event: ChangeEvent) {
        this.setVisibleComponents([this.layout.textboxCommentType], event.newValue === true);
        this.showCommentType(this.text(this.layout.textboxCommentType) || "");
    }

    textboxCommentTypeOnChange(event: ChangeEvent) {
        this.showCommentType(String(event.newValue ?? ""));
    }

    showCommentType(value: String) {
        const commentType = this.layout.textboxCommentType;
        this.setVisibleComponents([this.layout.textboxPostedType], commentType?.visible === true && value.toLowerCase().includes("history"));
    }

    checkboxCreateAvailableTractorsOnChange(event: ChangeEvent) {
        this.setVisibleComponents([this.layout.textboxTractorBrokerageCode], event.newValue === true);
    }

    checkboxEnableCarrierOfferOnChange(event: ChangeEvent) {
        const isEnabled = event.newValue === true;
        this.setVisibleComponents([
            this.layout.checkboxCreateAvailableTractorsOffer,
            this.layout.textboxOfferFailReasons,
            this.layout.panelOfferNotifications
        ], isEnabled);
    }

    checkboxEnableMilestonesOnChange(event: ChangeEvent) {
        const isEnabled = event.newValue === true;
        // Some layouts (Profile) may not contain checkboxStoreTemperature
        const storeTemp = this.layout instanceof FreightMatchingControl ? this.layout.checkboxStoreTemperature : undefined;
        const createCallin = this.layout instanceof FreightMatchingControl ? this.layout.checkboxCreateCallin : undefined;
        const createOrderHistComment = this.layout instanceof FreightMatchingControl ? this.layout.checkboxCreateOrderHistComment : undefined;
        const createMcPosition = this.layout instanceof FreightMatchingControl ? this.layout.checkboxCreateMcPosition : undefined;
        const tabsetProviders = this.layout instanceof FreightMatchingControl ? this.layout.tabsetProviders : undefined;
        this.setVisibleComponents([
            this.layout.checkboxEnableCarrierEquipment,
            this.layout.checkboxEnableCarrierDriver,
            this.layout.checkboxUseDfmEta,
            createCallin,
            createOrderHistComment,
            this.layout.checkboxAutoDispatch,
            this.layout.checkboxUpdateScheduledCallTime,
            storeTemp,
            createMcPosition,
            tabsetProviders
        ], isEnabled);
    }

    checkboxEnableLoadBookingOnChange(event: ChangeEvent) {
        const isEnabled = event.newValue === true;
        const visibilityComponents = [
            this.layout.checkboxEnabledCarrierUnassign,
            this.layout.textboxBrokerageCode,
            this.layout.textboxMovementStatus,
            this.layout.checkboxIsPayRateProvider,
            this.layout.checkboxSendArc,
            this.layout.textboxOfferCarrierWarning,
            this.layout.panelBookingNotifications,
            this.layout.checkboxOverridePayAmt
        ];
        const requiredComponents = [
            this.layout.textboxBrokerageCode,
            this.layout.textboxMovementStatus,
            this.layout.textboxOfferCarrierWarning
        ];
        this.setVisibleComponents(visibilityComponents, isEnabled);
        this.setRequiredComponents(requiredComponents, isEnabled);
        this.setRateConFieldVisibility();
        this.setMaxPayOverrideState();
        this.setRemoveCarrierPayVisiblity();
    }

    checkboxSendArcOnChange(event: ChangeEvent) {
        this.setRateConFieldVisibility();
    }

    private setRateConFieldVisibility() {
        const show = this.isChecked(this.layout.checkboxEnableLoadBooking) && this.isChecked(this.layout.checkboxSendArc);
        this.setVisibleComponents([
            this.layout.textboxAutoRateConfirmTemplate,
            this.layout.textboxRateConfirmationFrom,
            this.layout.checkboxNotifyArcFailure,
            this.layout.checkboxReceiveCcArc
        ], show);
    }

    checkboxIsPayRateProviderOnChange(event: ChangeEvent) {
        this.setMaxPayOverrideState();
    }

    private setMaxPayOverrideState() {
        const isVisibleAndRequired = this.isChecked(this.layout.checkboxIsPayRateProvider) && this.isChecked(this.layout.checkboxEnableLoadBooking);
        this.setVisibleComponents([
            this.layout.textboxOfferMaxPayOverride
        ], isVisibleAndRequired);
        this.setRequiredComponents([
            this.layout.textboxOfferMaxPayOverride
        ], isVisibleAndRequired);
    }

    checkboxEnabledCarrierUnassignOnChange(event: ChangeEvent) {
        this.setRemoveCarrierPayVisiblity();
    }

    private setRemoveCarrierPayVisiblity() {
        this.setVisibleComponents([
            this.layout.checkboxClearPayInfo
        ], this.isChecked(this.layout.checkboxEnabledCarrierUnassign) && this.isChecked(this.layout.checkboxEnableLoadBooking));
    }
}
